import { ArrowDown, ArrowLeftRight, Mail, MessageCircle, Phone, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { authedFetch } from "../lib/api";

const COPY = {
  directory: "TEST ENVIRONMENT — All clinicians and information shown here are fictional. Nothing generated here is saved or sent.",
  referrals: "TEST ENVIRONMENT — All SimplyRefer References shown here are fictional. Nothing is saved to a live account or sent to a clinician.",
  dashboard: "TEST ENVIRONMENT — This dashboard and all activity shown here are fictional. Nothing is saved to a live account.",
};

export const DemoBanner = ({ kind = "directory", linkMode = false, exitPath = "/clinicians", onExit }) => {
  const navigate = useNavigate();
  const [showPrelaunch, setShowPrelaunch] = useState(true);
  useEffect(() => {
    authedFetch("/site/launch-status?audience=demo").then(async (response) => {
      if (!response.ok) return;
      const data = await response.json();
      setShowPrelaunch(!data.is_live && data.show_banner !== false);
    }).catch(() => {});
  }, []);
  const handleExit = async () => {
    if (onExit) await onExit();
    navigate(exitPath);
  };
  return (
    <>
    {showPrelaunch && <div className="border-b border-amber-200 bg-[#FFF7E6] px-4 py-2 text-center text-sm font-semibold text-amber-950" data-testid="demo-prelaunch-banner">SimplyRefer is not live yet — this is a fictional demonstration and no live patient referral testing has started.</div>}
    <div className="border-b border-blue-700 bg-primary text-white" data-testid={"demo-" + kind + "-banner"}>
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <div>
          <p className="font-heading text-sm font-semibold">{COPY[kind] || COPY.directory}</p>
          <p className="mt-0.5 text-xs text-blue-100">Demo references remain available only for this browser session and expire when you exit.</p>
        </div>
        <Button size="sm" onClick={handleExit} className="bg-white text-primary hover:bg-white/90" data-testid="demo-exit-action">
          {linkMode ? <UserPlus className="mr-2 h-4 w-4" /> : <ArrowLeftRight className="mr-2 h-4 w-4" />}
          {linkMode ? "Create Free GP Account" : exitPath.startsWith("/admin") ? "Return to Admin" : "Return to live directory"}
        </Button>
      </div>
    </div>
    </>
  );
};

export const DemoCoachmark = ({ title, children, onSkip, onNext, nextLabel = "Continue", className = "" }) => (
  <div className={"rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm " + className} data-testid="demo-coachmark">
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-white">
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm font-semibold text-slate-900">{title}</p>
        <div className="mt-1 text-sm leading-relaxed text-slate-600">{children}</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {onNext && <Button type="button" size="sm" onClick={onNext}>{nextLabel}</Button>}
          {onSkip && <Button type="button" size="sm" variant="ghost" onClick={onSkip}>Skip guided tour</Button>}
        </div>
      </div>
    </div>
  </div>
);

export const DemoFooter = ({ linkMode = false, exitPath = "/clinicians", onExit }) => {
  const navigate = useNavigate();
  const handleAction = async () => {
    if (onExit) await onExit();
    navigate(exitPath);
  };
  return (
    <div className="mt-10 overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="demo-footer">
      {linkMode && (
        <div className="flex flex-col items-start justify-between gap-4 bg-gradient-to-r from-blue-50 to-slate-50 p-5 sm:flex-row sm:items-center">
          <div>
            <p className="font-heading text-lg font-semibold text-slate-900">Create your free GP account</p>
            <p className="mt-1 text-sm text-slate-600">Use SimplyRefer with the live directory when launch access is activated.</p>
          </div>
          <Button type="button" onClick={handleAction} data-testid="demo-footer-action">
            <UserPlus className="mr-2 h-4 w-4" /> Create Free GP Account
          </Button>
        </div>
      )}
      <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
        <span className="flex items-center gap-2 font-medium text-slate-800"><MessageCircle className="h-4 w-4 text-primary" /> Questions? Feedback?</span>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <a href="mailto:hello@simplyrefer.com.au" className="inline-flex items-center gap-1.5 hover:text-primary"><Mail className="h-4 w-4" /> hello@simplyrefer.com.au</a>
          <a href="tel:0428004444" className="inline-flex items-center gap-1.5 hover:text-primary"><Phone className="h-4 w-4" /> 0428 004 444</a>
        </div>
      </div>
    </div>
  );
};
